import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getWithTokenAPI } from "../../util/Api";
import {
  institution_dashboard,
  students_list_approved,
  students_list_autoapproved,
} from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import Firstphoto from "../../assets/uploadpage1.png";
import Scoundphoto from "../../assets/uploadpage2.png";
import Thirdphoto from "../../assets/uploadpage3.png";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import "../../styles/upload.css";

const COLORS = ["#059669", "#2563eb", "#8b5cf6", "#f59e0b", "#ec4899", "#3b82f6"];

const InstitutionDashboardComponent = () => {
  const { token, roleType, userId } = useSelector((state) => state.auth);
  const profile = useSelector((state) => state.profile);
  const navigate = useNavigate();

  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    totalBatches: 0,
    totalRevenue: 0,
  });

  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [revenueByExamData, setRevenueByExamData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Basic Dashboard Stats
      const resStats = await getWithTokenAPI(
        token,
        `${institution_dashboard}${userId}`
      );

      let calculatedRevenue = 0;
      let monthlyData = [];
      let examData = [];

      // 2. Fetch Subscription APIs for Revenue and Charts
      try {
        const instId = userId || "695f3f832929fa82230aaf33";
        const autoApprovedUrl = `${students_list_autoapproved}&institutionId=${instId}&limit=1000`;
        const approvedUrl = `${students_list_approved}&institutionId=${instId}&limit=1000`;

        const [resAuto, resApproved] = await Promise.all([
          getWithTokenAPI(token, autoApprovedUrl),
          getWithTokenAPI(token, approvedUrl),
        ]);

        const autoList = Array.isArray(resAuto?.data) ? resAuto.data : [];
        const approvedList = Array.isArray(resApproved?.data) ? resApproved.data : [];

        // Use ALL records from BOTH APIs — no deduplication
        // Each transaction is a payment so every record's amount must be counted
        const allItems = [...autoList, ...approvedList];
        const monthlyMap = {};
        const examMap = {};

        allItems.forEach((item) => {
          const price = Number(item.testPrice) || 0;
          calculatedRevenue += price;

          const exam = item.batchId?.examType?.value || "Other";
          examMap[exam] = (examMap[exam] || 0) + price;

          const dateObj = new Date(item.autoApprovedAt || item.createdAt || Date.now());
          if (!isNaN(dateObj.getTime())) {
            const mYear = dateObj.toLocaleDateString("en-IN", {
              month: "short",
              year: "numeric",
            });
            monthlyMap[mYear] = (monthlyMap[mYear] || 0) + price;
          }
        });

        monthlyData = Object.keys(monthlyMap).map((month) => ({
          month,
          revenue: monthlyMap[month],
        }));

        examData = Object.keys(examMap).map((exam) => ({
          name: exam,
          revenue: examMap[exam],
        }));
      } catch (revError) {
        console.error("Dashboard revenue fetch error:", revError);
      }

      setDashboardStats({
        totalStudents: resStats?.data?.totalStudents || 0,
        totalBatches: resStats?.data?.totalBatches || 0,
        totalRevenue: calculatedRevenue,
      });

      setMonthlyRevenueData(monthlyData);
      setRevenueByExamData(examData);
    } catch (error) {
      console.error("Error fetching institution dashboard data:", error);
      SafeToast.error("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchDashboardData();
    }
  }, [userId, token]);

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", fontFamily: "'Alata', sans-serif" }}>
   

      {/* 3 STAT CARDS ROW */}
      <div
        className="stats-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "24px",
          marginBottom: "28px",
        }}
      >
        <div
          className="stat-card-upload student"
          style={{ backgroundImage: `url(${Firstphoto})`, cursor: "pointer" }}
          onClick={() => navigate("/institution/students")}
        >
          <span className="stat-title">Total Students</span>
          <h2>{dashboardStats.totalStudents.toLocaleString()}</h2>
        </div>

        <div
          className="stat-card-upload batch"
          style={{ backgroundImage: `url(${Scoundphoto})`, cursor: "pointer" }}
          onClick={() => navigate("/batches")}
        >
          <span className="stat-title">Total Batches</span>
          <h2>{dashboardStats.totalBatches.toLocaleString()}</h2>
        </div>

        <div
          className="stat-card-upload revenue"
          style={{ backgroundImage: `url(${Thirdphoto})`, cursor: "pointer" }}
          onClick={() => navigate("/institution/revenue")}
        >
          <span className="stat-title">Total Revenue</span>
          <h2>₹{dashboardStats.totalRevenue.toLocaleString()}</h2>
        </div>
      </div>

      {/* ANALYTICS CHARTS ROW */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
          marginBottom: "28px",
        }}
      >
        {/* Monthly Revenue Bar Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "24px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "17px", color: "#0f172a", fontWeight: "600" }}>
            Monthly Revenue Growth (₹)
          </h3>
          {monthlyRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "50px", textAlign: "center", color: "#94a3b8" }}>No monthly revenue data available</div>
          )}
        </div>

        {/* Revenue Breakdown by Exam Type Pie Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "24px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "17px", color: "#0f172a", fontWeight: "600" }}>
            Revenue Breakdown by Exam Type
          </h3>
          {revenueByExamData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={revenueByExamData}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  dataKey="revenue"
                  label={({ name, revenue }) => `${name}: ₹${revenue}`}
                >
                  {revenueByExamData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "50px", textAlign: "center", color: "#94a3b8" }}>No exam revenue data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstitutionDashboardComponent;
